import { useSettings } from "../settings/context";
import { useFormSaving } from "./useFormSaving";
import { combineSaveState } from "./saveBadge";

/**
 * WHERE THE SAVING STANDS.
 *
 * There is no save button, so the only way an organiser can know their work is
 * safe is to be told. Silence would be indistinguishable from a broken pipe —
 * and the moment they find out would be the reload.
 *
 * ⚠️ A REFUSAL IS NOT A FAILURE. "Échec" promises a retry; a 401 has nothing to
 * retry — see `SyncState`. It gets its own wording, and the automatic cycle stops.
 *
 * ⚠️ "Échec" IS LOUD ON PURPOSE. The others are quiet: an organiser does not need
 * to watch a badge to work. A failure does need watching — nothing is lost, it
 * will go out on the next cycle, but if it keeps failing they must stop typing
 * rather than fill a form that leads nowhere.
 *
 * Nothing is shown in the seeded demonstration: there is no event to save to,
 * and a badge saying "enregistré" would be a lie.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */
/* No trailing dots. The spinner already says it is in progress; the ellipsis
   said it a second time, in a row where every character costs something.
   Short on purpose, too. This badge shares a row with three selectors and two
   buttons; "Modifications en attente" pushed "Voir en visiteur" off the screen at
   1280px — the organiser locked in edit mode, the very trap this bar already had
   to have closed once. The full sentence lives in `title`. */
const WORDING = {
  idle: "",
  waiting: "En attente",
  saving: "Enregistrement",
  saved: "Enregistré",
  failed: "Échec",
  // Court, comme les autres : la barre est étroite. La phrase est dans `title`.
  refused: "Refusé",
} as const;

const DETAIL = {
  idle: "",
  waiting: "Modifications en attente : elles partent dans quelques secondes.",
  saving: "Enregistrement en cours",
  saved: "Toutes vos modifications sont enregistrées.",
  failed:
    "L’enregistrement a échoué. Rien n’est perdu : une nouvelle tentative aura lieu automatiquement.",
  /* ⚠️ NE PROMET AUCUNE NOUVELLE TENTATIVE. Le serveur refuse d'enregistrer pour
     ce compte ; répéter l'appel ne change rien, et le dire reviendrait à faire
     travailler quelqu'un dans le vide. */
  refused:
    "Le serveur refuse d’enregistrer : ce compte n’a pas les droits sur cet événement. Vos modifications restent à l’écran, mais elles ne partiront pas.",
} as const;

export function SaveState() {
  const { saveState, saving, saveMode, unsaved, saveNow } = useSettings();
  /**
   * ⚠️ LES FORMULAIRES COMPTENT AVEC LES RÉGLAGES. Ils n'empruntent pas la même route
   * — la leur détruit et réinsère, elle ne fusionne pas — mais pour l'organisateur
   * c'est UNE seule question : « mon travail est-il parti ? ». Deux indicateurs et deux
   * boutons pour y répondre, c'était deux fois trop.
   */
  const { pending, send, state: formState } = useFormSaving();
  /**
   * ⚠️ UN SEUL ÉTAT, LU DES DEUX CÔTÉS. Le badge ne lisait que celui des réglages :
   * un formulaire enregistré seul passait de « en attente » à RIEN (relevé par William
   * le 2026-09-13), sans « Enregistrement » ni « Enregistré ». Voir `saveBadge.ts`.
   */
  const shown = combineSaveState(saveState, formState, pending);
  if (!saving || shown === "idle") return null;

  /**
   * In manual mode the badge becomes THE BUTTON. The organiser asked to decide
   * when their work is sent; a state they can only watch would leave them
   * hunting for the action somewhere else.
   */
  if (
    saveMode === "manual" &&
    (unsaved || pending > 0 || formState === "saving")
  ) {
    return (
      <button
        type="button"
        className="obtn obtn--save"
        data-missing={
          shown === "failed" || shown === "refused" ? undefined : "1"
        }
        disabled={shown === "saving"}
        title={
          shown === "refused"
            ? DETAIL.refused
            : shown === "failed"
              ? "L’envoi a échoué. Rien n’est perdu : réessayez."
              : "Envoyer les modifications maintenant"
        }
        onClick={() => {
          void saveNow();
          void send();
        }}
      >
        {shown === "saving" ? (
          <>
            <span className="spin" aria-hidden="true" />
            Enregistrement
          </>
        ) : shown === "refused" ? (
          "Enregistrement refusé"
        ) : shown === "failed" ? (
          "Réessayer l’enregistrement"
        ) : (
          "Enregistrer"
        )}
      </button>
    );
  }

  return (
    <span
      className="savestate"
      data-s={shown}
      role="status"
      title={DETAIL[shown]}
    >
      {/* A spinner while it is actually in flight, a dot the rest of the time.
          The same `.spin` the buttons use — one thing that means "working", so an
          organiser learns it once. */}
      {shown === "saving" ? (
        <span className="spin" aria-hidden="true" />
      ) : (
        <i aria-hidden="true" />
      )}
      <b>{WORDING[shown]}</b>
    </span>
  );
}
